import React, { useEffect, useRef, useState } from "react";
import {
  Crop, X, Copy, Download, Share2, Check, Loader2, Pin, Ruler as RulerIcon, Paperclip, Plus,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * CropTool — quick-crop / screenshot export.
 *
 * Goal: faster than taking a screenshot manually.
 *
 * Flow:
 *  1. User activates crop mode → SheetCanvas freezes interaction and
 *     forwards a `onCropDrag(rect)` callback while the user drags a
 *     selection rectangle.
 *  2. On release, we render a floating action bar with Copy / Download
 *     PNG / Download JPEG / Share buttons.
 *  3. Export pipeline: load the source raster JPEG → off-screen canvas →
 *     paint the selected region (optionally overlaid with pins/markups) →
 *     export as blob.
 *
 * NOT a document-export workflow. Field communication tooling — one click
 * → in someone's text message.
 */

/* ---------------------------------------------------------------- */
/* Crop selection visual — sibling rendered inside the SheetCanvas    */
/* overlay slot. Receives normalized (0..1) selection rect.           */
/* ---------------------------------------------------------------- */
export function CropSelectionLayer({ rect, imgDims, viewScale, active }) {
  if (!active || !rect) return null;
  const W = imgDims?.w || 1;
  const H = imgDims?.h || 1;
  const { nx1, ny1, nx2, ny2 } = rect;
  const x = Math.min(nx1, nx2) * W;
  const y = Math.min(ny1, ny2) * H;
  const w = Math.abs(nx2 - nx1) * W;
  const h = Math.abs(ny2 - ny1) * H;
  if (w < 1 || h < 1) {
    // Tiny / hovering — still render a crosshair-style anchor.
    return (
      <svg
        className="absolute inset-0 pointer-events-none"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        data-testid="crop-selection-svg"
      >
        <circle cx={nx1 * W} cy={ny1 * H} r={4 / viewScale} fill="var(--fop-primary)" />
      </svg>
    );
  }
  // Darkened outside-mask via 4 rects.
  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      data-testid="crop-selection-svg"
    >
      {/* Outside mask — 4 rects covering everything outside the selection */}
      <rect x={0} y={0} width={W} height={y} fill="rgba(10,10,11,0.55)" />
      <rect x={0} y={y + h} width={W} height={Math.max(0, H - (y + h))} fill="rgba(10,10,11,0.55)" />
      <rect x={0} y={y} width={x} height={h} fill="rgba(10,10,11,0.55)" />
      <rect x={x + w} y={y} width={Math.max(0, W - (x + w))} height={h} fill="rgba(10,10,11,0.55)" />
      {/* Border */}
      <rect
        x={x} y={y} width={w} height={h}
        fill="none"
        stroke="var(--fop-primary)"
        strokeWidth={1.5 / viewScale}
        vectorEffect="non-scaling-stroke"
      />
      {/* Corner handles */}
      {[
        [x, y], [x + w, y], [x, y + h], [x + w, y + h],
      ].map(([cx, cy], i) => (
        <rect
          key={i}
          x={cx - 5 / viewScale} y={cy - 5 / viewScale}
          width={10 / viewScale} height={10 / viewScale}
          fill="#0a0a0b"
          stroke="var(--fop-primary)"
          strokeWidth={1 / viewScale}
        />
      ))}
    </svg>
  );
}

/* ---------------------------------------------------------------- */
/* CropControls — floating action bar shown when a crop is finalized */
/* ---------------------------------------------------------------- */
export default function CropControls({
  active, rect, sheet, rasterUrl, onClose, onReset,
  includePins, setIncludePins,
  includeMeasurements, setIncludeMeasurements,
  renderOverlayToBlob, // callback supplied by SheetViewer to render pins/measure overlays
  pins = [], // existing pins on this sheet (for Attach-to-Pin picker)
  onPinPhotoAttached,  // callback (pin_id) → parent refreshes pin list
}) {
  const [busy, setBusy] = useState(null); // 'copy' | 'png' | 'jpeg' | 'share' | 'attach' | null
  const [done, setDone] = useState(null); // same enum
  const [error, setError] = useState(null);
  const [showPinPicker, setShowPinPicker] = useState(false);

  if (!active) return null;

  const hasSelection = rect && (
    Math.abs((rect.nx2 || 0) - (rect.nx1 || 0)) > 0.005 &&
    Math.abs((rect.ny2 || 0) - (rect.ny1 || 0)) > 0.005
  );

  const generateBlob = async (mime = "image/png", quality = 0.92) => {
    if (!rect || !rasterUrl) return null;
    // Load the source raster JPEG.
    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.crossOrigin = "anonymous";
      im.onload = () => resolve(im);
      im.onerror = (e) => reject(e);
      im.src = rasterUrl;
    });
    const nx1 = Math.min(rect.nx1, rect.nx2);
    const ny1 = Math.min(rect.ny1, rect.ny2);
    const nx2 = Math.max(rect.nx1, rect.nx2);
    const ny2 = Math.max(rect.ny1, rect.ny2);
    const sx = nx1 * img.naturalWidth;
    const sy = ny1 * img.naturalHeight;
    const sw = (nx2 - nx1) * img.naturalWidth;
    const sh = (ny2 - ny1) * img.naturalHeight;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(sw);
    canvas.height = Math.round(sh);
    const ctx = canvas.getContext("2d");
    // Draw base raster region.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    // Optional overlay layer (pins / measure) — caller renders into the canvas.
    if (renderOverlayToBlob) {
      try {
        await renderOverlayToBlob(ctx, {
          sx, sy, sw, sh,
          dw: canvas.width, dh: canvas.height,
          includePins, includeMeasurements,
        });
      } catch (err) {
        console.warn("Overlay render skipped:", err);
      }
    }
    return new Promise((resolve) => canvas.toBlob(resolve, mime, quality));
  };

  const filename = (ext) => {
    const num = sheet?.sheet_number?.replace(/[^\w-]/g, "_") || "sheet";
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    return `${num}_crop_${stamp}.${ext}`;
  };

  const handleCopy = async () => {
    setError(null); setBusy("copy"); setDone(null);
    try {
      const blob = await generateBlob("image/png");
      if (!blob) throw new Error("No selection");
      if (navigator.clipboard?.write && typeof window.ClipboardItem !== "undefined") {
        await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
        setDone("copy");
      } else {
        throw new Error("Clipboard unavailable — try Download instead.");
      }
    } catch (e) {
      setError(e?.message || "Copy failed");
    } finally {
      setBusy(null);
      setTimeout(() => setDone(null), 1600);
    }
  };

  const handleDownload = async (kind) => {
    setError(null); setBusy(kind); setDone(null);
    try {
      const mime = kind === "jpeg" ? "image/jpeg" : "image/png";
      const blob = await generateBlob(mime, kind === "jpeg" ? 0.92 : 1);
      if (!blob) throw new Error("No selection");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename(kind === "jpeg" ? "jpg" : "png");
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setDone(kind);
    } catch (e) {
      setError(e?.message || "Download failed");
    } finally {
      setBusy(null);
      setTimeout(() => setDone(null), 1600);
    }
  };

  const handleShare = async () => {
    setError(null); setBusy("share"); setDone(null);
    try {
      const blob = await generateBlob("image/png");
      if (!blob) throw new Error("No selection");
      const file = new File([blob], filename("png"), { type: "image/png" });
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: sheet?.sheet_number || "Sheet crop",
          text: `${sheet?.sheet_number || ""} · ${sheet?.sheet_title || ""}`,
        });
        setDone("share");
      } else {
        // Fallback to clipboard
        await handleCopy();
      }
    } catch (e) {
      if (e?.name !== "AbortError") setError(e?.message || "Share failed");
    } finally {
      setBusy(null);
      setTimeout(() => setDone(null), 1600);
    }
  };

  /**
   * Attach the cropped image to a pin (existing or new).
   *
   * The screenshot remains the hero interaction — this is a tiny inline
   * bridge to give the crop a home on the drawing, NOT a workflow system.
   * Reuses the existing POST /api/pins/{id}/photos endpoint with the JPEG
   * blob. For "+ new pin here", we first POST /api/pins with the crop's
   * center as the anchor, then attach.
   */
  const handleAttachToPin = async (pinChoice) => {
    setError(null); setBusy("attach"); setDone(null);
    try {
      const blob = await generateBlob("image/jpeg", 0.92);
      if (!blob) throw new Error("No selection");

      let targetPinId = pinChoice?.id;
      // "+ new pin here" path
      if (!targetPinId) {
        if (!sheet) throw new Error("No sheet");
        const cx = (rect.nx1 + rect.nx2) / 2;
        const cy = (rect.ny1 + rect.ny2) / 2;
        const titleHint = `Crop · ${sheet?.sheet_number || ""}`;
        const r = await api.post("/pins", {
          sheet_id: sheet.id,
          page: 1,
          x: cx, y: cy,
          category: "photo_note", priority: "medium",
          title: titleHint,
          description: "Attached from quick crop",
        });
        targetPinId = r.data.id;
      }

      // Upload the JPEG to that pin.
      const fd = new FormData();
      fd.append("file", new File([blob], filename("jpg"), { type: "image/jpeg" }));
      await api.post(`/pins/${targetPinId}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setDone("attach");
      setShowPinPicker(false);
      if (onPinPhotoAttached) onPinPhotoAttached(targetPinId);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Attach failed");
    } finally {
      setBusy(null);
      setTimeout(() => setDone(null), 1800);
    }
  };

  return (
    <div
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[55] rounded-lg shadow-2xl border border-[var(--fop-border-hi)] bg-[var(--fop-surface)] flex items-stretch overflow-hidden"
      data-testid="crop-controls"
      onPointerDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-r border-[var(--fop-border)] min-w-[140px]">
        <Crop size={13} className="text-[var(--fop-primary)]" />
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">
            Quick crop
          </div>
          <div className="text-[11px] text-[var(--fop-text)] font-mono leading-tight">
            {!hasSelection ? "Drag a rectangle" : "Ready"}
          </div>
        </div>
      </div>

      {/* Overlay toggles */}
      {hasSelection && (
        <div className="flex items-stretch border-r border-[var(--fop-border)]">
          <button
            onClick={() => setIncludePins((v) => !v)}
            className={`px-2 text-[11px] font-mono uppercase tracking-wider flex items-center gap-1 ${
              includePins ? "text-[var(--fop-primary)] bg-[var(--fop-primary-soft)]" : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
            }`}
            data-testid="crop-include-pins"
            title="Include pins"
          >
            <Pin size={11} />
          </button>
          <button
            onClick={() => setIncludeMeasurements((v) => !v)}
            className={`px-2 text-[11px] font-mono uppercase tracking-wider flex items-center gap-1 border-l border-[var(--fop-border)] ${
              includeMeasurements ? "text-[var(--fop-primary)] bg-[var(--fop-primary-soft)]" : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
            }`}
            data-testid="crop-include-measure"
            title="Include measurements"
          >
            <RulerIcon size={11} />
          </button>
        </div>
      )}

      {hasSelection && (
        <>
          <ActionBtn
            onClick={handleCopy} busy={busy === "copy"} done={done === "copy"}
            icon={Copy} label="Copy" testid="crop-copy"
          />
          <ActionBtn
            onClick={() => handleDownload("png")} busy={busy === "png"} done={done === "png"}
            icon={Download} label="PNG" testid="crop-download-png"
          />
          <ActionBtn
            onClick={() => handleDownload("jpeg")} busy={busy === "jpeg"} done={done === "jpeg"}
            icon={Download} label="JPG" testid="crop-download-jpeg"
          />
          {typeof navigator !== "undefined" && navigator.share && (
            <ActionBtn
              onClick={handleShare} busy={busy === "share"} done={done === "share"}
              icon={Share2} label="Share" testid="crop-share"
            />
          )}
          <ActionBtn
            onClick={() => setShowPinPicker((v) => !v)}
            busy={busy === "attach"}
            done={done === "attach"}
            icon={Paperclip}
            label="Attach"
            testid="crop-attach-pin"
          />
          <button
            onClick={onReset}
            className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)] border-r border-[var(--fop-border)]"
            data-testid="crop-reset"
          >
            Reset
          </button>
        </>
      )}

      <button
        onClick={onClose}
        className="px-3 py-2 text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
        data-testid="crop-close"
        title="Close (Esc)"
      >
        <X size={13} />
      </button>
      {error && (
        <div className="absolute -top-9 left-1/2 -translate-x-1/2 px-2 py-1 rounded bg-[var(--st-escalated)] text-white text-[10px] font-mono" data-testid="crop-error">
          {error}
        </div>
      )}
      {/* Attach-to-pin floating picker — pin chips of existing pins on this
          sheet + "+ new pin here". Lightweight, inline, contextual; no
          workflow / form expansion. The screenshot remains the hero. */}
      {showPinPicker && hasSelection && (
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-[300px] max-h-[260px] overflow-y-auto rounded-md shadow-2xl border border-[var(--fop-border-hi)] bg-[var(--fop-surface)] p-2 z-[60]"
          data-testid="crop-attach-picker"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-1 pb-1.5 mb-1 border-b border-[var(--fop-border)]">
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">
              Attach crop to pin
            </div>
            <button
              onClick={() => setShowPinPicker(false)}
              className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
              data-testid="crop-attach-picker-close"
              aria-label="Close picker"
            >
              <X size={12} />
            </button>
          </div>
          <button
            onClick={() => handleAttachToPin(null)}
            disabled={busy === "attach"}
            data-testid="crop-attach-new-pin"
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-[var(--fop-surface-2)] disabled:opacity-50"
          >
            <Plus size={12} className="text-[var(--fop-primary)]" />
            <div className="font-mono text-[11px] text-[var(--fop-text)]">
              + new pin here
            </div>
            <div className="ml-auto font-mono text-[9px] text-[var(--fop-text-3)] uppercase tracking-wider">
              at crop center
            </div>
          </button>
          {pins && pins.length > 0 && (
            <div className="mt-1 pt-1 border-t border-[var(--fop-border)]">
              <div className="px-1 pb-1 font-mono text-[8px] uppercase tracking-wider text-[var(--fop-text-3)]">
                Existing on this sheet
              </div>
              {pins.slice(0, 30).map((p) => (
                <button
                  key={p.id}
                  onClick={() => handleAttachToPin(p)}
                  disabled={busy === "attach"}
                  data-testid={`crop-attach-pin-${p.id}`}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-[var(--fop-surface-2)] disabled:opacity-50"
                >
                  <Pin size={11} className="text-[var(--fop-text-3)] shrink-0" />
                  <div className="font-mono text-[10px] text-[var(--fop-text)] truncate">
                    {p.title || "Untitled pin"}
                  </div>
                  {p.status && (
                    <div className="ml-auto font-mono text-[8px] uppercase tracking-wider text-[var(--fop-text-3)]">
                      {p.status}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
          {busy === "attach" && (
            <div className="absolute inset-0 bg-[var(--fop-surface)]/80 flex items-center justify-center" data-testid="crop-attach-busy">
              <Loader2 size={16} className="text-[var(--fop-primary)] animate-spin" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ActionBtn({ onClick, busy, done, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className="px-2.5 py-2 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] hover:text-[var(--fop-primary)] hover:bg-[var(--fop-surface-2)] border-r border-[var(--fop-border)] flex items-center gap-1 disabled:opacity-50"
    >
      {busy ? <Loader2 size={11} className="animate-spin" />
        : done ? <Check size={11} className="text-[var(--st-corrected)]" />
        : <Icon size={11} />}
      {label}
    </button>
  );
}
